/**
 * RoasTimeWidget - Виджет ROAS по времени
 * =========================================
 * 
 * Показывает как меняется ROAS когорт с течением времени
 * с разбивкой по источникам дохода (InApps Android/iOS, AdMon Android/iOS)
 * и возможностью просмотра как общего ROAS, так и по отдельным кампаниям
 */

import React, { useMemo } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useFilters } from '../../hooks/useFilters';
import { useDashboard } from '../../contexts/DashboardContext';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { CampaignIcons } from '../ui/brand-icons';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { subDays, format as formatDate } from 'date-fns';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { roas as roasSpecs, roasTime as roasTimeSpecs } from '@ggstat/chart-specs';
import { 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  BarChart3, 
  LineChart as LineChartIcon, 
  AreaChart as AreaChartIcon,
  Target,
  Layers,
  ChevronRight,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';

// Цвета и подписи источников дохода
const REVENUE_COLORS = Object.fromEntries(roasTimeSpecs.revenueSeries.map(s => [s.source, s.color]));
const REVENUE_LABELS = Object.fromEntries(roasTimeSpecs.revenueSeries.map(s => [s.source, s.label]));

// Цвета для кампаний
const CAMPAIGN_COLORS = roasSpecs.campaignColors;

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Кастомная легенда для режима "Общий ROAS"
 */
function RevenueSourceLegend({ enabledLines, setEnabledLines }) {
  return (
    <div className="flex justify-center gap-6 mt-4">
      {Object.keys(REVENUE_COLORS).map(key => (
        <div
          key={key}
          onClick={() => setEnabledLines(prev => ({ ...prev, [key]: !prev[key] }))}
          className="flex items-center gap-2 cursor-pointer select-none"
          style={{ opacity: enabledLines[key] ? 1 : 0.4 }}
        >
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: REVENUE_COLORS[key] }}
          />
          <span className="text-sm">{REVENUE_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Компонент графика ROAS - режим "Общий ROAS"
 */
function TotalRoasChart({ data, chartType, enabledLines, setEnabledLines, groupBy }) {
  const xAxisConfig = getXAxisConfig(groupBy, data.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const nonZeroPayload = payload.filter(entry => entry.value > 0);
      const formattedLabel = formatTooltipDate(label, groupBy);
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          {nonZeroPayload.map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
            return (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {entry.name}: {entry.value.toFixed(2)}% ({percentage}%)
              </p>
            );
          })}
          <div className="border-t mt-2 pt-2">
            <p className="text-sm font-bold">
              Total ROAS: {total.toFixed(2)}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  if (chartType === 'bar') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip order="stack" content={<CustomTooltip />} />
            <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
            {enabledLines.inappsAndroid && (
              <Bar 
                dataKey="roasInappsAndroid" 
                name="InApps Android"
                fill={REVENUE_COLORS.inappsAndroid}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.inappsIos && (
              <Bar 
                dataKey="roasInappsIos" 
                name="InApps iOS"
                fill={REVENUE_COLORS.inappsIos}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonAndroid && (
              <Bar 
                dataKey="roasAdmonAndroid" 
                name="AdMon Android"
                fill={REVENUE_COLORS.admonAndroid}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonIos && (
              <Bar 
                dataKey="roasAdmonIos" 
                name="AdMon iOS"
                fill={REVENUE_COLORS.admonIos}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
        <RevenueSourceLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
      </div>
    );
  }
  
  if (chartType === 'area') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip order="stack" content={<CustomTooltip />} />
            <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
            {enabledLines.inappsAndroid && (
              <Area 
                type="monotone" 
                dataKey="roasInappsAndroid" 
                name="InApps Android"
                stroke={REVENUE_COLORS.inappsAndroid}
                fill={REVENUE_COLORS.inappsAndroid}
                fillOpacity={0.6}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.inappsIos && (
              <Area 
                type="monotone" 
                dataKey="roasInappsIos" 
                name="InApps iOS"
                stroke={REVENUE_COLORS.inappsIos}
                fill={REVENUE_COLORS.inappsIos}
                fillOpacity={0.6}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonAndroid && (
              <Area 
                type="monotone" 
                dataKey="roasAdmonAndroid" 
                name="AdMon Android"
                stroke={REVENUE_COLORS.admonAndroid}
                fill={REVENUE_COLORS.admonAndroid}
                fillOpacity={0.6}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonIos && (
              <Area 
                type="monotone" 
                dataKey="roasAdmonIos" 
                name="AdMon iOS"
                stroke={REVENUE_COLORS.admonIos}
                fill={REVENUE_COLORS.admonIos}
                fillOpacity={0.6}
                stackId="roas"
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        <RevenueSourceLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
      </div>
    );
  }
  
  // Default: line chart
  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
          {enabledLines.inappsAndroid && (
            <Line 
              type="monotone" 
              dataKey="roasInappsAndroid" 
              name="InApps Android"
              stroke={REVENUE_COLORS.inappsAndroid}
              strokeWidth={2}
              dot={{ fill: REVENUE_COLORS.inappsAndroid, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.inappsIos && (
            <Line 
              type="monotone" 
              dataKey="roasInappsIos" 
              name="InApps iOS"
              stroke={REVENUE_COLORS.inappsIos}
              strokeWidth={2}
              dot={{ fill: REVENUE_COLORS.inappsIos, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.admonAndroid && (
            <Line 
              type="monotone" 
              dataKey="roasAdmonAndroid" 
              name="AdMon Android"
              stroke={REVENUE_COLORS.admonAndroid}
              strokeWidth={2}
              dot={{ fill: REVENUE_COLORS.admonAndroid, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.admonIos && (
            <Line 
              type="monotone" 
              dataKey="roasAdmonIos" 
              name="AdMon iOS"
              stroke={REVENUE_COLORS.admonIos}
              strokeWidth={2}
              dot={{ fill: REVENUE_COLORS.admonIos, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      <RevenueSourceLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
    </div>
  );
}

/**
 * Компонент графика ROAS - режим "По кампаниям"
 */
function CampaignsRoasChart({ chartData, campaigns, visibleCampaigns, campaignColors, chartType, groupBy, campaignMetadata }) {
  const xAxisConfig = getXAxisConfig(groupBy, chartData.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const sortedPayload = payload;
      const formattedLabel = formatTooltipDate(label, groupBy);
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          <div className="space-y-1">
            {sortedPayload.map((entry, index) => {
              const campaignName = entry.dataKey;
              const metadata = campaignMetadata?.[campaignName];
              
              return (
                <div key={index} className="text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <CampaignIcons 
                      source={metadata?.source} 
                      platform={metadata?.platform}
                      size={14}
                      gap={1}
                    />
                    <p style={{ color: entry.color }} className="font-medium">
                      {campaignName}
                    </p>
                  </div>
                  <p>
                    <span className="text-muted-foreground">ROAS:</span>{' '}
                    <span className={entry.value >= 100 ? 'text-green-600' : 'text-amber-600'}>
                      {entry.value.toFixed(2)}%
                    </span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };
  
  if (chartType === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={500}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip order="series" content={<CustomTooltip />} />
          <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
          {campaigns.map((campaign, index) => {
            if (!visibleCampaigns[campaign]) return null;
            return (
              <Bar
                key={campaign}
                dataKey={campaign}
                fill={campaignColors[campaign]}
                isAnimationActive={false}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    );
  }
  
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={500}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
          {campaigns.map((campaign) => {
            if (!visibleCampaigns[campaign]) return null;
            return (
              <Area
                key={campaign}
                type="monotone"
                dataKey={campaign}
                stroke={campaignColors[campaign]}
                fill={campaignColors[campaign]}
                fillOpacity={0.6}
                isAnimationActive={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  
  // Default: line chart
  return (
    <ResponsiveContainer width="100%" height={500}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="period"
          tickFormatter={(value) => formatChartDate(value, groupBy)}
          {...xAxisConfig}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" label="100%" />
        {campaigns.map((campaign) => {
          if (!visibleCampaigns[campaign]) return null;
          return (
            <Line
              key={campaign}
              type="monotone"
              dataKey={campaign}
              stroke={campaignColors[campaign]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Таблица кампаний для режима "По кампаниям"
 */
function CampaignsTable({ campaigns, campaignTotals, visibleCampaigns, onToggleCampaign, campaignColors, campaignMetadata }) {
  const activeCampaigns = campaigns
    .filter(c => (campaignTotals[c]?.cost || 0) > 0)
    .sort((a, b) => (campaignTotals[b]?.roas || 0) - (campaignTotals[a]?.roas || 0));
  const zeroCostCampaigns = campaigns.filter(c => (campaignTotals[c]?.cost || 0) === 0);
  
  const [showZeroCost, setShowZeroCost] = React.useState(false);
  
  const allActiveVisible = activeCampaigns.length > 0 && activeCampaigns.every(c => visibleCampaigns[c] === true);
  
  const renderCampaignRow = (campaign, isZeroCost = false) => {
    const campaignTotalsData = campaignTotals[campaign];
    const color = campaignColors[campaign];
    
    return (
      <tr key={campaign} className={`border-t hover:bg-muted/50 ${isZeroCost ? 'bg-muted/20' : ''}`}>
        <td className="py-2 px-2">
          <Checkbox 
            checked={visibleCampaigns[campaign] === true}
            onCheckedChange={(checked) => onToggleCampaign(campaign, checked)}
          />
        </td>
        <td className="py-2 px-2">
          <div className="flex items-center gap-2">
            <CampaignIcons 
              source={campaignMetadata?.[campaign]?.source} 
              platform={campaignMetadata?.[campaign]?.platform}
              size={16}
              gap={2}
            />
            <span 
              className={`font-medium cursor-pointer hover:opacity-70 transition-opacity ${isZeroCost ? 'text-muted-foreground' : ''}`}
              style={{ color: isZeroCost ? undefined : color }}
              onClick={() => onToggleCampaign(campaign, !visibleCampaigns[campaign])}
            >
              {campaign}
            </span>
          </div>
        </td>
        <td className="text-right py-2 px-2 font-medium">
          {MetricsService.formatCurrency(campaignTotalsData?.cost || 0)}
        </td>
        <td className="text-right py-2 px-2 font-medium">
          {MetricsService.formatCurrency(campaignTotalsData?.revenue || 0)}
        </td>
        <td className="text-right py-2 px-2">
          <span className={`font-bold ${
            (campaignTotalsData?.roas || 0) >= 100 
              ? 'text-green-600' 
              : 'text-amber-600'
          }`}>
            {(campaignTotalsData?.roas || 0).toFixed(2)}%
          </span>
        </td>
      </tr>
    );
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 [&_th]:bg-card [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="border-b">
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                  <Checkbox 
                    checked={allActiveVisible}
                    onCheckedChange={(checked) => {
                      activeCampaigns.forEach(campaign => onToggleCampaign(campaign, checked));
                    }}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">Кампания</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Затраты</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Доход</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {activeCampaigns.map((campaign) => renderCampaignRow(campaign, false))}
              
              {zeroCostCampaigns.length > 0 && (
                <>
                  <tr className="border-t bg-muted/30">
                    <td colSpan="5" className="py-2 px-2">
                      <button
                        onClick={() => setShowZeroCost(!showZeroCost)}
                        className="flex items-center gap-2 w-full text-left hover:text-primary transition-colors"
                      >
                        {showZeroCost ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-xs font-medium text-muted-foreground">
                          Кампании без затрат ({zeroCostCampaigns.length})
                        </span>
                      </button>
                    </td>
                  </tr>
                  {showZeroCost && zeroCostCampaigns.map((campaign) => 
                    renderCampaignRow(campaign, true)
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Основной компонент виджета
 */
export function RoasTimeWidget() {
  const { platform, startDate } = useFilters();
  const { includeOrganic } = useDashboard();
  
  const [groupBy, setGroupBy] = useLocalStorage('roas-time-widget-groupBy', 'day');
  const [totalChartType, setTotalChartType] = useLocalStorage('roas-time-widget-total-chartType', 'bar');
  const [campaignChartType, setCampaignChartType] = useLocalStorage('roas-time-widget-campaign-chartType', 'line');
  const [dayFromInstall, setDayFromInstall] = useLocalStorage('roas-time-widget-dayFromInstall', 7);
  const [viewMode, setViewMode] = useLocalStorage('roas-time-widget-viewMode', 'total');
  const [customDay, setCustomDay] = React.useState('');
  
  const [enabledLines, setEnabledLines] = useLocalStorage('roas-time-widget-enabledLines', {
    inappsAndroid: true,
    inappsIos: true,
    admonAndroid: true,
    admonIos: true
  });
  
  const [visibleCampaigns, setVisibleCampaigns] = React.useState({});
  
  const presetDays = roasTimeSpecs.presetDays;
  
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  const splitByCampaign = viewMode === 'campaigns';
  const widgetData = useWidgetData(
    async (filters, options) => {
      const response = await MetricsService.getRoasByPeriod(
        filters,
        groupBy,
        dayFromInstall,
        splitByCampaign,
        includeOrganic,
        options
      );
      return response;
    },
    [groupBy, dayFromInstall, splitByCampaign, includeOrganic]
  );
  
  const widgetProps = useBaseWidget(widgetData);
  const { data: roasData } = widgetProps;
  
  // Когорта D{N} существует только если с даты установки прошло ≥ N дней
  const emptyStateMessage = useMemo(() => {
    if (!roasData || !Array.isArray(roasData.data) || roasData.data.length > 0) return null;
    const latestEligibleInstallDate = subDays(new Date(), dayFromInstall);
    const start = startDate ? new Date(startDate) : null;
    if (start && start > latestEligibleInstallDate) {
      return `Для D${dayFromInstall} ROAS нужны когорты с датой установки не позже ${formatDate(latestEligibleInstallDate, 'yyyy-MM-dd')} (≥ ${dayFromInstall} дней назад). Сдвиньте начало периода раньше или выберите меньший день.`;
    }
    return 'Нет данных ROAS за выбранный период.';
  }, [roasData, dayFromInstall, startDate]);

  const processedData = useMemo(() => {
    if (!roasData) return null;

    if (!shouldApplyFees) return roasData;

    const { data, totals, ...rest } = roasData;

    if (!data || !Array.isArray(data)) return roasData;

    const adjustedData = data.map(item => {
      const adjustedItem = { ...item };
      
      if (platform === 'android' || platform === 'all') {
        const androidRevenues = [{ revenue: item.revenueInappsAndroid }];
        const adjusted = MetricsService.applyPlatformFees(androidRevenues, true, 'android');
        adjustedItem.revenueInappsAndroid = adjusted[0].revenue;
        adjustedItem.roasInappsAndroid = item.totalCost > 0 ? (adjusted[0].revenue / item.totalCost) * 100 : 0;
      }
      
      if (platform === 'ios' || platform === 'all') {
        const iosRevenues = [{ revenue: item.revenueInappsIos }];
        const adjusted = MetricsService.applyPlatformFees(iosRevenues, true, 'ios');
        adjustedItem.revenueInappsIos = adjusted[0].revenue;
        adjustedItem.roasInappsIos = item.totalCost > 0 ? (adjusted[0].revenue / item.totalCost) * 100 : 0;
      }
      
      const totalRevenue = adjustedItem.revenueInappsAndroid + adjustedItem.revenueInappsIos + 
                          adjustedItem.revenueAdmonAndroid + adjustedItem.revenueAdmonIos;
      adjustedItem.roasTotal = item.totalCost > 0 ? (totalRevenue / item.totalCost) * 100 : 0;
      
      if (adjustedItem.campaigns) {
        const newCampaigns = {};
        Object.entries(adjustedItem.campaigns).forEach(([campaign, campaignData]) => {
          const adjustedCampaign = { ...campaignData };
          
          if (platform === 'android' || platform === 'all') {
            const androidRevenues = [{ revenue: campaignData.revenueInappsAndroid }];
            const adjusted = MetricsService.applyPlatformFees(androidRevenues, true, 'android');
            adjustedCampaign.revenueInappsAndroid = adjusted[0].revenue;
          }
          
          if (platform === 'ios' || platform === 'all') {
            const iosRevenues = [{ revenue: campaignData.revenueInappsIos }];
            const adjusted = MetricsService.applyPlatformFees(iosRevenues, true, 'ios');
            adjustedCampaign.revenueInappsIos = adjusted[0].revenue;
          }
          
          const campaignRevenue = adjustedCampaign.revenueInappsAndroid + adjustedCampaign.revenueInappsIos + 
                                 adjustedCampaign.revenueAdmonAndroid + adjustedCampaign.revenueAdmonIos;
          adjustedCampaign.roasTotal = campaignData.cost > 0 ? (campaignRevenue / campaignData.cost) * 100 : 0;
          
          newCampaigns[campaign] = adjustedCampaign;
        });
        adjustedItem.campaigns = newCampaigns;
      }
      
      return adjustedItem;
    });

    const totalRevenue = adjustedData.reduce((sum, item) => 
      sum + item.revenueInappsAndroid + item.revenueInappsIos + 
      item.revenueAdmonAndroid + item.revenueAdmonIos, 0
    );
    const avgRoas = totals.totalCost > 0 ? (totalRevenue / totals.totalCost) * 100 : 0;

    return {
      ...rest,
      data: adjustedData,
      totals: {
        ...totals,
        avgRoas: MetricsService.roundValue(avgRoas),
        totalRevenue: MetricsService.roundValue(totalRevenue)
      },
    };
  }, [roasData, shouldApplyFees, platform]);
  
  const { chartData, campaigns, campaignColors, campaignTotals, campaignMetadata } = useMemo(() => {
    if (!processedData?.data || viewMode !== 'campaigns') {
      return { chartData: [], campaigns: [], campaignColors: {}, campaignTotals: {}, campaignMetadata: {} };
    }
    
    const campaignsSet = new Set();
    processedData.data.forEach(item => {
      if (item.campaigns) {
        Object.keys(item.campaigns).forEach(c => campaignsSet.add(c));
      }
    });
    const campaignsList = Array.from(campaignsSet).sort();
    
    const colors = {};
    campaignsList.forEach((campaign, index) => {
      colors[campaign] = CAMPAIGN_COLORS[index % CAMPAIGN_COLORS.length];
    });
    
    const chartDataMap = {};
    processedData.data.forEach(item => {
      if (!chartDataMap[item.period]) {
        chartDataMap[item.period] = { period: item.period };
      }
      if (item.campaigns) {
        Object.entries(item.campaigns).forEach(([campaign, data]) => {
          chartDataMap[item.period][campaign] = data.roasTotal;
        });
      }
    });
    
    const chartDataArray = Object.values(chartDataMap).sort((a, b) => a.period.localeCompare(b.period));
    
    const totals = {};
    campaignsList.forEach(campaign => {
      let totalCost = 0;
      let totalRevenue = 0;
      
      processedData.data.forEach(item => {
        if (item.campaigns && item.campaigns[campaign]) {
          const c = item.campaigns[campaign];
          totalCost += c.cost;
          totalRevenue += c.revenueInappsAndroid + c.revenueInappsIos + 
                         c.revenueAdmonAndroid + c.revenueAdmonIos;
        }
      });
      
      totals[campaign] = {
        cost: totalCost,
        revenue: totalRevenue,
        roas: totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0
      };
    });
    
    return { 
      chartData: chartDataArray, 
      campaigns: campaignsList, 
      campaignColors: colors,
      campaignTotals: totals,
      campaignMetadata: processedData.campaignMetadata || {}
    };
  }, [processedData, viewMode]);
  
  const campaignsKey = campaigns.join(',');
  React.useEffect(() => {
    if (campaigns.length > 0 && Object.keys(campaignTotals).length > 0) {
      const newState = {};
      campaigns.forEach(campaign => {
        const hasNoCost = (campaignTotals[campaign]?.cost || 0) === 0;
        newState[campaign] = !hasNoCost;
      });
      setVisibleCampaigns(newState);
    }
    // Намеренно зависим только от campaignsKey: сбрасываем видимость при смене НАБОРА
    // кампаний, а не на каждом рефетче — иначе затираются ручные галочки пользователя.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsKey]);
  
  const handleToggleCampaign = (campaign, checked) => {
    setVisibleCampaigns(prev => ({
      ...prev,
      [campaign]: checked
    }));
  };
  
  const Controls = () => (
    <div className="flex items-center gap-4 flex-wrap">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily"><Calendar className="h-4 w-4 mr-1" />Daily</ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly"><Calendar className="h-4 w-4 mr-1" />Weekly</ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly"><Calendar className="h-4 w-4 mr-1" />Monthly</ToggleGroupItem>
      </ToggleGroup>
      <div className="h-6 w-px bg-border" />
      <ToggleGroup 
        type="single" 
        value={viewMode === 'total' ? totalChartType : campaignChartType} 
        onValueChange={(value) => {
          if (value) {
            if (viewMode === 'total') {
              setTotalChartType(value);
            } else {
              setCampaignChartType(value);
            }
          }
        }}
      >
        <ToggleGroupItem value="line" aria-label="Line Chart"><LineChartIcon className="h-4 w-4" /></ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart"><BarChart3 className="h-4 w-4" /></ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart"><AreaChartIcon className="h-4 w-4" /></ToggleGroupItem>
      </ToggleGroup>
      <div className="h-6 w-px bg-border" />
      <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value)}>
        <ToggleGroupItem value="total" aria-label="Total ROAS"><TrendingUp className="h-4 w-4 mr-1" />Общий ROAS</ToggleGroupItem>
        <ToggleGroupItem value="campaigns" aria-label="By Campaigns"><Layers className="h-4 w-4 mr-1" />По кампаниям</ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
  
  return (
    <BaseWidget
      {...widgetProps}
      title="ROAS Over Time"
      subtitle={processedData?.totals ? `Avg ROAS: ${processedData.totals.avgRoas.toFixed(2)}%` : null}
      headerAction={<Controls />}
      emptyMessage="No ROAS data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        <Card>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">ROAS Day</label>
                <span className="text-sm text-muted-foreground">Showing ROAS for Day {dayFromInstall}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <ToggleGroup type="single" value={String(dayFromInstall)} onValueChange={(value) => value && setDayFromInstall(parseInt(value))} className="flex-wrap">
                  {presetDays.map(day => (
                    <ToggleGroupItem key={day} value={String(day)} className="px-3 py-1.5 text-sm">D{day}</ToggleGroupItem>
                  ))}
                </ToggleGroup>
                <div className="h-6 w-px bg-border mx-1" />
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={customDay}
                  onChange={(e) => setCustomDay(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && customDay) {
                      const day = parseInt(customDay);
                      if (!isNaN(day) && day >= 0) setDayFromInstall(day);
                      setCustomDay('');
                    }
                  }}
                  onBlur={() => {
                    if (customDay) {
                      const day = parseInt(customDay);
                      if (!isNaN(day) && day >= 0) setDayFromInstall(day);
                      setCustomDay('');
                    }
                  }}
                  placeholder="Custom..."
                  className="w-24 px-3 py-1.5 text-sm border rounded-md bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ MozAppearance: 'textfield' }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {emptyStateMessage && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{emptyStateMessage}</AlertDescription>
          </Alert>
        )}

        {!emptyStateMessage && processedData?.totals && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Target} label="Total Cohort Size" value={processedData.totals.totalCohortSize.toLocaleString()} subtitle="Users in period" />
            <StatCard icon={DollarSign} label="Total Cost" value={MetricsService.formatCurrency(processedData.totals.totalCost)} subtitle="Ad spend" />
            <StatCard icon={TrendingUp} label="Average ROAS" value={`${processedData.totals.avgRoas.toFixed(2)}%`} subtitle={processedData.totals.avgRoas >= 100 ? 'Profitable' : 'Not profitable'} />
          </div>
        )}
        
        {processedData?.data && processedData.data.length > 0 && (
          <div className="mt-6">
            {viewMode === 'total' ? (
              <TotalRoasChart data={processedData.data} chartType={totalChartType} enabledLines={enabledLines} setEnabledLines={setEnabledLines} groupBy={groupBy} />
            ) : (
              <>
                <CampaignsRoasChart chartData={chartData} campaigns={campaigns} visibleCampaigns={visibleCampaigns} campaignColors={campaignColors} chartType={campaignChartType} groupBy={groupBy} campaignMetadata={campaignMetadata} />
                {campaigns.length > 0 && (
                  <div className="mt-6">
                    <CampaignsTable campaigns={campaigns} campaignTotals={campaignTotals} visibleCampaigns={visibleCampaigns} onToggleCampaign={handleToggleCampaign} campaignColors={campaignColors} campaignMetadata={campaignMetadata} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default RoasTimeWidget;
